let bound = false;

const csrf = (): string =>
  (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content || "";

function renderName(editor: HTMLElement, value: string): void {
  const nameEl = editor.querySelector<HTMLElement>("[data-company-name]");
  if (!nameEl) return;
  const original = editor.dataset.original || "";
  if (value && value !== original) {
    const strike = document.createElement("strike");
    strike.textContent = original;
    nameEl.textContent = "";
    nameEl.append(strike, ` ${value}`);
  } else {
    nameEl.textContent = original;
  }
}

function commit(editor: HTMLElement, value: string): void {
  const original = editor.dataset.original || "";
  const prev = editor.dataset.value || "";

  editor.dataset.value = value || original;
  renderName(editor, value);
  editor.classList.add("saving");

  fetch(editor.dataset.url || "", {
    method: "PATCH",
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrf(),
      Accept: "application/json",
    },
    // Typing the original name back (or clearing the input) clears the override.
    body: JSON.stringify({
      bots_prospect: { user_provided_company_name: value === original ? "" : value },
    }),
  })
    .then((r) => {
      if (!r.ok) throw new Error(String(r.status));
    })
    .catch(() => {
      editor.dataset.value = prev;
      renderName(editor, prev);
    })
    .finally(() => editor.classList.remove("saving"));
}

function beginEdit(editor: HTMLElement): void {
  if (editor.querySelector("input")) return;
  const nameEl = editor.querySelector<HTMLElement>("[data-company-name]");
  const pen = editor.querySelector<HTMLElement>(".co-name-pen");
  if (!nameEl || !pen) return;

  const input = document.createElement("input");
  input.type = "text";
  input.className = "co-name-input";
  input.value = editor.dataset.value || "";
  nameEl.style.display = "none";
  pen.style.display = "none";
  editor.appendChild(input);
  input.focus();
  input.select();

  // The click that dismisses the editor must not also trigger row-level
  // handlers (the candidate drawer opens on any row click). An outside
  // pointerdown arms the swallow; the matching click is stopped in capture.
  // Keyboard dismissals never arm it, so later clicks pass through untouched.
  let outsidePress = false;
  const onPointerDown = (e: Event): void => {
    if (input.contains(e.target as Node)) return;
    outsidePress = true;
  };
  const swallowClick = (e: Event): void => {
    if (!outsidePress) return;
    e.stopPropagation();
    cleanup();
  };
  const cleanup = (): void => {
    document.removeEventListener("pointerdown", onPointerDown, true);
    document.removeEventListener("click", swallowClick, true);
  };
  document.addEventListener("pointerdown", onPointerDown, true);
  document.addEventListener("click", swallowClick, true);

  let done = false;
  const finish = (save: boolean): void => {
    if (done) return;
    done = true;
    if (!outsidePress) cleanup();
    const value = input.value.trim();
    input.remove();
    nameEl.style.display = "";
    pen.style.display = "";
    if (save && value !== (editor.dataset.value || "")) commit(editor, value);
  };

  input.addEventListener("blur", () => finish(true));
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      input.blur();
    } else if (e.key === "Escape") {
      finish(false);
    }
  });
}

export function initCompanyNameEditor(): void {
  if (bound) return;
  bound = true;

  document.addEventListener("click", (e) => {
    const pen = (e.target as HTMLElement).closest<HTMLElement>(".co-name-pen");
    if (!pen) return;
    e.preventDefault();
    const editor = pen.closest<HTMLElement>("[data-company-editor]");
    if (editor) beginEdit(editor);
  });
}
